// src/App.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import './App.css';

// Import cache utilities for development
import './utils/cacheUtils';
import { clearInvalidPriceData, deleteCachedData, CACHE_KEYS } from './services/cache';

// Import types
import {
  FearGreedIndex,
  MarketCapData,
  GoogleTrendsData,
  RSIData,
  BeaconchainData,
  NewsData,
  AggregatedWalletData,
  MarketHeatScore,
  DataRefreshStatus,
  DashboardSettings,
  MarketHeatHistoryEntry,
  HistoryTimeRange
} from './types';

// Import API service
import apiService from './services/api';

// Import utilities
import { calculateMarketHeatScore } from './utils/marketHeat';

// Import market heat history service
import { getMarketHeatHistory, getEthPriceHistory, EthPriceHistoryEntry } from './services/marketHeatHistory';

// Import constants
import {
  DEFAULT_DASHBOARD_SETTINGS
} from './constants';

// Import components
import Header from './components/Header';
import MarketHeatGauge from './components/MarketHeatGauge';
import MarketHeatHistoryChart from './components/MarketHeatHistoryChart';
import FearGreedCard from './components/cards/FearGreedCard';
import MarketCapCard from './components/cards/MarketCapCard';
import GoogleTrendsCard from './components/cards/GoogleTrendsCard'; // Will be used as a self-contained embed
import RSICard from './components/cards/RSICard';
import BeaconchainCard from './components/cards/BeaconchainCard';
import NewsCard from './components/cards/NewsCard';
import WalletActivityCard from './components/cards/WalletActivityCard';
import SettingsPanel from './components/SettingsPanel';
import Footer from './components/Footer';
import UpdateStatusIndicator from './components/UpdateStatusIndicator';
import ErrorBoundary from './components/ErrorBoundary';
import RefreshButton from './components/RefreshButton';

const App: React.FC = () => {
  // State for all data sources
  const [fearGreedIndex, setFearGreedIndex] = useState<FearGreedIndex | null>(null);
  const [marketCap, setMarketCap] = useState<MarketCapData | null>(null);
  const [googleTrends, setGoogleTrends] = useState<GoogleTrendsData | null>(null);
  const [ethereumRSI, setEthereumRSI] = useState<RSIData | null>(null);
  const [beaconchain, setBeaconchain] = useState<BeaconchainData | null>(null);
  const [newsData, setNewsData] = useState<NewsData | null>(null);
  const [walletData, setWalletData] = useState<AggregatedWalletData | null>(null);

  // Market heat score
  const [marketHeatScore, setMarketHeatScore] = useState<MarketHeatScore | null>(null);

  // Market heat history state
  const [marketHeatHistory, setMarketHeatHistory] = useState<MarketHeatHistoryEntry[]>([]);
  const [ethPriceHistory, setEthPriceHistory] = useState<EthPriceHistoryEntry[]>([]);
  const [historyTimeRange, setHistoryTimeRange] = useState<HistoryTimeRange>('1y');
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // UI state
  const [settings, setSettings] = useState<DashboardSettings>(DEFAULT_DASHBOARD_SETTINGS);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [refreshStatus, setRefreshStatus] = useState<DataRefreshStatus>({
    marketData: { lastRefreshed: 0, isLoading: false },
    newsData: { lastRefreshed: 0, isLoading: false },
    walletData: { lastRefreshed: 0, isLoading: false }
  });
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  // Individual loading states for specific components
  const [loadingStates, setLoadingStates] = useState({
    fearGreed: false,
    marketCap: false,
    googleTrends: false,
    ethereumRSI: false,
    beaconchain: false,
    news: false,
    wallets: false
  });

  // Theme state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(
    settings.displayOptions.theme === 'dark' ||
    (settings.displayOptions.theme === 'system' &&
     window.matchMedia('(prefers-color-scheme: dark)').matches)
  );

  // Function to fetch fear and greed index
  const fetchFearGreedIndex = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, fearGreed: true }));
      setRefreshStatus(prev => ({
        ...prev,
        marketData: { ...prev.marketData, isLoading: true }
      }));

      const data = await apiService.fetchFearGreedIndex();
      setFearGreedIndex(data);
      setErrors(prev => ({ ...prev, fearGreed: null }));
    } catch (error) {
      console.error('Error fetching Fear & Greed Index:', error);
      setErrors(prev => ({ ...prev, fearGreed: 'Failed to load Fear & Greed Index' }));
    } finally {
      setLoadingStates(prev => ({ ...prev, fearGreed: false }));
      setRefreshStatus(prev => ({
        ...prev,
        marketData: {
          lastRefreshed: Date.now(),
          isLoading: false
        }
      }));
    }
  }, []);

  // Function to fetch market cap data
  const fetchMarketCap = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, marketCap: true }));
      setRefreshStatus(prev => ({
        ...prev,
        marketData: { ...prev.marketData, isLoading: true }
      }));

      const data = await apiService.fetchMarketCapData({
        timeframe: settings.displayOptions.chartTimeframe
      });
      setMarketCap(data);
      setErrors(prev => ({ ...prev, marketCap: null }));
    } catch (error) {
      console.error('Error fetching Market Cap:', error);
      setErrors(prev => ({ ...prev, marketCap: 'Failed to load Market Cap data' }));
    } finally {
      setLoadingStates(prev => ({ ...prev, marketCap: false }));
      setRefreshStatus(prev => ({
        ...prev,
        marketData: {
          lastRefreshed: Date.now(),
          isLoading: false
        }
      }));
    }
  }, [settings.displayOptions.chartTimeframe]);

  // Function to fetch Google Trends data via API
  const fetchGoogleTrends = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, googleTrends: true }));
      setRefreshStatus(prev => ({
        ...prev,
        marketData: { ...prev.marketData, isLoading: true }
      }));

      // Clear cached Google Trends data to force fresh API call with corrected algorithm
      await deleteCachedData(`${CACHE_KEYS.GOOGLE_TRENDS}_SPX6900 price_7d`);

      // Use API service to fetch real Google Trends data
      const data = await apiService.fetchGoogleTrends('SPX6900 price');
      console.log('🔍 Google Trends API returned:', {
        value: data?.value,
        relativeValue: data?.relativeValue,
        fullData: data
      });
      setGoogleTrends(data);
      setErrors(prev => ({ ...prev, googleTrends: null }));
    } catch (error) {
      console.error('Error fetching Google Trends:', error);
      setErrors(prev => ({ ...prev, googleTrends: 'Failed to load Google Trends data' }));
    } finally {
      setLoadingStates(prev => ({ ...prev, googleTrends: false }));
      setRefreshStatus(prev => ({
        ...prev,
        marketData: {
          lastRefreshed: Date.now(),
          isLoading: false
        }
      }));
    }
  }, []);

  // Function to fetch Ethereum RSI
  const fetchEthereumRSI = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, ethereumRSI: true }));
      setRefreshStatus(prev => ({
        ...prev,
        marketData: { ...prev.marketData, isLoading: true }
      }));

      const data = await apiService.fetchEthereumRSI('1d', 14);
      setEthereumRSI(data);
      setErrors(prev => ({ ...prev, ethereumRSI: null }));
    } catch (error) {
      console.error('Error fetching Ethereum RSI:', error);
      setErrors(prev => ({ ...prev, ethereumRSI: 'Failed to load Ethereum RSI data' }));
    } finally {
      setLoadingStates(prev => ({ ...prev, ethereumRSI: false }));
      setRefreshStatus(prev => ({
        ...prev,
        marketData: {
          lastRefreshed: Date.now(),
          isLoading: false
        }
      }));
    }
  }, []);

  // Function to fetch Beaconchain data
  const fetchBeaconchain = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, beaconchain: true }));
      setRefreshStatus(prev => ({
        ...prev,
        marketData: { ...prev.marketData, isLoading: true }
      }));

      const data = await apiService.fetchBeaconchainData();
      setBeaconchain(data);
      setErrors(prev => ({ ...prev, beaconchain: null }));
    } catch (error) {
      console.error('Error fetching Beaconchain data:', error);
      setErrors(prev => ({ ...prev, beaconchain: 'Failed to load Beaconchain data' }));
    } finally {
      setLoadingStates(prev => ({ ...prev, beaconchain: false }));
      setRefreshStatus(prev => ({
        ...prev,
        marketData: {
          lastRefreshed: Date.now(),
          isLoading: false
        }
      }));
    }
  }, []);

  // Function to fetch news data
  const fetchNewsData = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, news: true }));
      setRefreshStatus(prev => ({
        ...prev,
        newsData: { ...prev.newsData, isLoading: true }
      }));

      const data = await apiService.fetchNewsData({
        timeframe: '1w'
      });
      setNewsData(data);
      setErrors(prev => ({ ...prev, newsData: null }));
    } catch (error) {
      console.error('Error fetching News data:', error);
      setErrors(prev => ({ ...prev, newsData: 'Failed to load News data' }));
    } finally {
      setLoadingStates(prev => ({ ...prev, news: false }));
      setRefreshStatus(prev => ({
        ...prev,
        newsData: {
          lastRefreshed: Date.now(),
          isLoading: false
        }
      }));
    }
  }, []);

  // Function to fetch wallet data
  const fetchWalletData = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, wallets: true }));
      setRefreshStatus(prev => ({
        ...prev,
        walletData: { ...prev.walletData, isLoading: true }
      }));

      const data = await apiService.fetchSPX6900WhaleActivity({ // Updated function call
        minAgeInDays: settings.walletTracking.minAgeInDays,
        minBalanceUSD: settings.walletTracking.minBalanceUSD,
        networks: settings.walletTracking.trackedNetworks
      });
      setWalletData(data);
      setErrors(prev => ({ ...prev, walletData: null }));
    } catch (error) {
      console.error('Error fetching Wallet data:', error);
      setErrors(prev => ({ ...prev, walletData: 'Failed to load Wallet data' }));
    } finally {
      setLoadingStates(prev => ({ ...prev, wallets: false }));
      setRefreshStatus(prev => ({
        ...prev,
        walletData: {
          lastRefreshed: Date.now(),
          isLoading: false
        }
      }));
    }
  }, [
    settings.walletTracking.minAgeInDays,
    settings.walletTracking.minBalanceUSD,
    settings.walletTracking.trackedNetworks
  ]);

  // Function to refresh all data
  const refreshAllData = useCallback(async () => {
    await Promise.all([
      fetchFearGreedIndex(),
      fetchMarketCap(),
      fetchGoogleTrends(),
      fetchEthereumRSI(),
      fetchBeaconchain(),
      fetchNewsData(),
      fetchWalletData()
    ]);
  }, [
    fetchFearGreedIndex,
    fetchMarketCap,
    fetchGoogleTrends,
    fetchEthereumRSI,
    fetchBeaconchain,
    fetchNewsData,
    fetchWalletData
  ]);

  // Function to fetch market heat history and ETH price
  const fetchMarketHeatHistory = useCallback(async (range: HistoryTimeRange) => {
    try {
      setHistoryLoading(true);
      setHistoryError(null);

      // Fetch both heat history and ETH price in parallel
      const [history, ethPrices] = await Promise.all([
        getMarketHeatHistory(range),
        getEthPriceHistory(range)
      ]);

      setMarketHeatHistory(history);
      setEthPriceHistory(ethPrices);
    } catch (error) {
      console.error('Error fetching market heat history:', error);
      setHistoryError('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Fetch history on mount and when time range changes
  useEffect(() => {
    fetchMarketHeatHistory(historyTimeRange);
  }, [historyTimeRange, fetchMarketHeatHistory]);

  // Calculate market heat score whenever data changes
  useEffect(() => {
    if (fearGreedIndex || marketCap || googleTrends || ethereumRSI ||
        beaconchain || newsData || walletData) {
      const score = calculateMarketHeatScore(
        fearGreedIndex,
        marketCap,
        googleTrends,
        ethereumRSI,
        beaconchain,
        null, // ethOpenInterest removed
        null, // spx6900OpenInterest removed
        newsData,
        walletData
      );

      setMarketHeatScore(score);
    }
  }, [
    fearGreedIndex,
    marketCap,
    googleTrends,
    ethereumRSI,
    beaconchain,
    newsData,
    walletData
  ]);

  // Set up automatic refresh intervals
  useEffect(() => {
    // Clean up any invalid cached price data first
    clearInvalidPriceData().catch(console.error);
    
    // Initial data fetch
    refreshAllData();

    // Set up refresh intervals
    const marketDataInterval = setInterval(() => {
      fetchFearGreedIndex();
      fetchMarketCap();
      // fetchGoogleTrends(); // Removed
      fetchEthereumRSI();
      fetchBeaconchain();
    }, settings.refreshRates.marketData);
    
    const newsDataInterval = setInterval(() => {
      fetchNewsData();
    }, settings.refreshRates.newsData);

    const walletDataInterval = setInterval(() => {
      fetchWalletData();
    }, settings.refreshRates.walletData);

    // Clean up intervals on unmount
    return () => {
      clearInterval(marketDataInterval);
      clearInterval(newsDataInterval);
      clearInterval(walletDataInterval);
    };
  }, [
    refreshAllData,
    fetchFearGreedIndex,
    fetchMarketCap,
    // fetchGoogleTrends, // Removed
    fetchEthereumRSI,
    fetchBeaconchain,
    fetchNewsData,
    fetchWalletData,
    settings.refreshRates
  ]);

  // Handle theme changes
  useEffect(() => {
    const isDark = settings.displayOptions.theme === 'dark' ||
      (settings.displayOptions.theme === 'system' &&
       window.matchMedia('(prefers-color-scheme: dark)').matches);

    setIsDarkMode(isDark);

    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Listen for system theme changes if using system setting
    if (settings.displayOptions.theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = (e: MediaQueryListEvent) => {
        setIsDarkMode(e.matches);
        if (e.matches) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [settings.displayOptions.theme]);

  // Toggle settings panel
  const toggleSettings = () => {
    setSettingsOpen(!settingsOpen);
  };

  // Update settings
  const updateSettings = (newSettings: Partial<DashboardSettings>) => {
    setSettings(prev => ({
      ...prev,
      ...newSettings
    }));
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp: number) => {
    return format(timestamp, 'MMM d, yyyy HH:mm:ss');
  };

  // Calculate if any data is currently loading
  const isLoading =
    refreshStatus.marketData.isLoading ||
    refreshStatus.newsData.isLoading ||
    refreshStatus.walletData.isLoading;

  // Determine grid layout class based on settings
  const gridLayoutClass = settings.displayOptions.layout === 'grid'
    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
    : 'flex flex-col space-y-4';

  // Determine density class based on settings
  const densityClass = {
    compact: 'p-2',
    comfortable: 'p-4',
    spacious: 'p-6'
  }[settings.displayOptions.density];

  return (
    <div className={`App min-h-screen ${isDarkMode ? 'dark bg-dashboard-background-dark text-dashboard-text-dark' : 'bg-dashboard-background-light text-dashboard-text-light'}`}>
      <Header
        title="Crypto Top Signals Dashboard"
        isDarkMode={isDarkMode}
        onToggleTheme={() => updateSettings({
          displayOptions: {
            ...settings.displayOptions,
            theme: isDarkMode ? 'light' : 'dark'
          }
        })}
        onToggleSettings={toggleSettings}
      />

      <main className={`container mx-auto ${densityClass}`}>
        <ErrorBoundary>
          {/* Market Heat Score Section */}
          <section className="mb-8">
            <div className="flex flex-col md:flex-row justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">
                Market Heat Score
              </h2>
              <RefreshButton
                onClick={refreshAllData}
                isLoading={isLoading}
                lastRefreshed={Math.max(
                  refreshStatus.marketData.lastRefreshed,
                  refreshStatus.newsData.lastRefreshed,
                  refreshStatus.walletData.lastRefreshed
                )}
                formatTimestamp={formatTimestamp}
              />
            </div>

            <div className="bg-dashboard-card-light dark:bg-dashboard-card-dark rounded-card shadow-card p-6">
              <MarketHeatGauge
                score={marketHeatScore?.overall || 50}
                interpretation={marketHeatScore?.interpretation || 'Neutral'}
                components={marketHeatScore?.components}
              />
            </div>

            {/* Market Heat History Chart */}
            <div className="mt-6">
              <MarketHeatHistoryChart
                data={marketHeatHistory}
                ethPriceData={ethPriceHistory}
                loading={historyLoading}
                error={historyError}
                timeRange={historyTimeRange}
                onTimeRangeChange={setHistoryTimeRange}
              />
            </div>
          </section>

          {/* Dashboard Cards Grid */}
          <section className={gridLayoutClass}>
            {/* Market Data Cards */}
            <FearGreedCard
              data={fearGreedIndex}
              loading={refreshStatus.marketData.isLoading}
              error={errors.fearGreed}
              lastUpdated={refreshStatus.marketData.lastRefreshed}
              formatTimestamp={formatTimestamp}
              dataStatus={errors.fearGreed ? 'error' : (fearGreedIndex ? 'live' : 'error')}
            />

            <MarketCapCard
              data={marketCap}
              loading={refreshStatus.marketData.isLoading}
              error={errors.marketCap}
              lastUpdated={refreshStatus.marketData.lastRefreshed}
              formatTimestamp={formatTimestamp}
              timeframe={settings.displayOptions.chartTimeframe}
              dataStatus={errors.marketCap ? 'error' : (marketCap ? 'live' : 'error')}
            />

            <GoogleTrendsCard
              trendValue={googleTrends?.value}
              lastUpdated={refreshStatus.marketData.lastRefreshed}
              dataStatus={errors.googleTrends ? 'error' : (googleTrends ? 'live' : 'error')}
            /> 

            <RSICard
              data={ethereumRSI}
              loading={refreshStatus.marketData.isLoading}
              error={errors.ethereumRSI}
              lastUpdated={refreshStatus.marketData.lastRefreshed}
              formatTimestamp={formatTimestamp}
              overboughtThreshold={settings.alertThresholds.rsiOverbought}
              oversoldThreshold={settings.alertThresholds.rsiOversold}
              dataStatus={errors.ethereumRSI ? 'error' : (ethereumRSI ? 'live' : 'error')}
            />

            <BeaconchainCard
              data={beaconchain}
              loading={refreshStatus.marketData.isLoading}
              error={errors.beaconchain}
              lastUpdated={refreshStatus.marketData.lastRefreshed}
              formatTimestamp={formatTimestamp}
              dataStatus={errors.beaconchain ? 'error' : (beaconchain ? 'live' : 'error')}
            />
            
            <NewsCard
              data={newsData}
              loading={refreshStatus.newsData.isLoading}
              error={errors.newsData}
              lastUpdated={refreshStatus.newsData.lastRefreshed}
              formatTimestamp={formatTimestamp}
              dataStatus={errors.newsData ? 'error' : (newsData ? 'live' : 'error')}
            />

            <WalletActivityCard
              data={walletData}
              loading={refreshStatus.walletData.isLoading}
              error={errors.walletData}
              lastUpdated={refreshStatus.walletData.lastRefreshed}
              formatTimestamp={formatTimestamp}
              significantMovementThreshold={settings.alertThresholds.significantWalletMovement}
              dataStatus={errors.walletData ? 'error' : (walletData ? 'live' : 'error')}
            />
          </section>
        </ErrorBoundary>
      </main>

      {/* Settings Panel */}
      {settingsOpen && (
        <SettingsPanel
          settings={settings}
          onClose={toggleSettings}
          onUpdate={updateSettings}
        />
      )}

      {/* Update Status Indicator */}
      <UpdateStatusIndicator status={loadingStates} />

      <Footer />
    </div>
  );
};

export default App;
